/**
 * BrandIdentityTab
 *
 * Tab "Identidad" del Manual de tu IA. Combina:
 *  1. Datos de la marca (read-only, vienen del CRM Deenex Multisucursal)
 *  2. Auto-aprendizaje desde sitio web o brand guidelines PDF
 *
 * La identidad es de SOLA LECTURA — se administra en el CRM. Lo que sí
 * editable acá es cómo la IA infiere voz/tono/agresividad desde fuentes
 * externas (BrandLearning) que después aplican a la tab Personalidad.
 */

import {
    Building2,
    MapPin,
    Store,
    Globe,
    Palette,
    Users,
    UtensilsCrossed,
    Lock,
} from "lucide-react";
import BrandLearning from "./BrandLearning";
import type { DeenexMultiMarca } from "../../../hooks/useIntegracion";

interface BrandIdentityTabProps {
    marca: DeenexMultiMarca | null;
    onApplyLearning: (r: {
        tone: number;
        aggressiveness: number;
        addForbidden: string[];
    }) => void;
}

export default function BrandIdentityTab({
    marca,
    onApplyLearning,
}: BrandIdentityTabProps) {
    const name = marca?.appName || "Tu marca";
    const initials = name
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const vertical = (marca as any)?.vertical;
    const localesCount = (marca as any)?.localesCount;
    const activeUsers = (marca as any)?.activeUsers30d;

    return (
        <div className="space-y-6">
            {/* ─── Card: datos de la marca (read-only) ─── */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-slate-100 text-slate-600">
                            <Building2 size={14} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-900">
                                Datos de la marca
                            </h3>
                            <p className="text-[10px] text-slate-500">
                                Vienen del CRM Deenex Multisucursal
                            </p>
                        </div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500 flex items-center gap-1">
                        <Lock size={9} />
                        Solo lectura
                    </span>
                </div>

                <div className="p-5">
                    {/* Logo + nombre */}
                    <div className="flex items-center gap-4 mb-5">
                        {marca?.appLogo || marca?.images?.logo ? (
                            <img
                                src={marca.appLogo || marca.images?.logo}
                                alt={name}
                                className="w-20 h-20 rounded-2xl object-cover bg-white shadow-md ring-2 ring-white border border-slate-200"
                            />
                        ) : (
                            <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-md ring-2 ring-white bg-gradient-to-br from-violet-500 to-fuchsia-500">
                                {initials}
                            </div>
                        )}
                        <div className="min-w-0">
                            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                                {name}
                            </h2>
                            {vertical && (
                                <p className="text-sm text-slate-600 mt-0.5">
                                    {vertical}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Grid de datos */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <InfoCell
                            icon={<Globe size={12} />}
                            label="Dominio"
                            value={marca?.domain || "—"}
                        />
                        <InfoCell
                            icon={<Store size={12} />}
                            label="Locales"
                            value={
                                localesCount
                                    ? `${localesCount} sucursales`
                                    : "—"
                            }
                        />
                        <InfoCell
                            icon={<Users size={12} />}
                            label="Usuarios activos"
                            value={
                                activeUsers
                                    ? `${activeUsers.toLocaleString("es-AR")} (30d)`
                                    : "—"
                            }
                        />
                        <InfoCell
                            icon={<UtensilsCrossed size={12} />}
                            label="Sistemas"
                            value={
                                marca?.systemOptions
                                    ? [
                                          marca.systemOptions.deliverySystem
                                              ? "Delivery"
                                              : null,
                                          marca.systemOptions.tableSystem
                                              ? "Salón"
                                              : null,
                                      ]
                                          .filter(Boolean)
                                          .join(" · ")
                                    : "—"
                            }
                        />
                    </div>

                    {/* Colores de marca */}
                    {marca?.colors && marca.colors.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                                <Palette size={11} />
                                Paleta de marca
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {marca.colors.map((c) => (
                                    <div
                                        key={c.name}
                                        className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 rounded-lg border border-slate-200"
                                    >
                                        <div
                                            className="w-4 h-4 rounded shadow-sm border border-white"
                                            style={{
                                                backgroundColor: c.value,
                                            }}
                                        />
                                        <span className="text-[11px] font-mono text-slate-700">
                                            {c.value}
                                        </span>
                                        <span className="text-[10px] text-slate-400">
                                            ({c.name})
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Help text */}
                    <p className="text-[11px] text-slate-400 italic mt-5 leading-relaxed">
                        💡 Estos datos los administra el dueño de la marca desde
                        Deenex Multisucursal. La IA los usa automáticamente para
                        personalizar mensajes y configurar el dominio.
                    </p>
                </div>
            </div>

            {/* ─── BrandLearning ─── */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-base font-bold text-slate-900">
                            Aprender estilo automáticamente
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Pegá tu URL o subí un PDF de brand guidelines — la IA
                            extrae voz, tono y palabras a evitar.
                        </p>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-violet-100 text-violet-700">
                        ✨ IA
                    </span>
                </div>
                <BrandLearning onApply={onApplyLearning} />
            </div>
        </div>
    );
}

function InfoCell({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) {
    return (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                <span className="text-slate-400">{icon}</span>
                {label}
            </div>
            <p className="text-sm font-semibold text-slate-900 truncate">
                {value}
            </p>
        </div>
    );
}
